/**
 * Country x Month pages — evergreen SEO pages for "{destination} in {month}" queries.
 *
 * Programmatic provider loops destinations x 12 months, only generates pages
 * for months with >= 2 events.
 */

import { getEventsForCountry, getCountryByCode, getEventsForNatureByCountry, getAllEvents } from './data-source';
import { registerEvergreenProvider, type EvergreenSlugEntry, getLanguagesForCountry, getPhraseEntry, getTemplateStr, getCountryNameEntry } from './i18n';
import { registerEvergreenReadiness } from './slug-evergreen';
import { registerEvergreenRenderer } from './evergreen-pages';
import { CALENDAR_YEAR } from './config';
import { SITE_NAME } from './brand';
import { MONTHS } from './constants';
import { MONTH_NAMES } from './hub-pages';
import { CountryMonthSeoSection } from '@/components/seo/CountryMonthSeoSection';
import { getSlugForCountry } from './slug-country';
import type { CountryCode, CalendarEvent } from './types';

const YEAR = CALENDAR_YEAR;

// A country×month page is only generated when the month has at least this many
// events — fewer would be thin content. MonthNavigation must use the same rule
// (via getGeneratedMonths) so it never links to an ungenerated month.
const MIN_EVENTS_FOR_PAGE = 2;

// ---------------------------------------------------------------------------
// Destination definitions
// ---------------------------------------------------------------------------

interface DestinationDef {
  slugPrefix: string; // e.g. 'bali', 'thailand'
  displayName: string;
  countryCode: CountryCode;
}

const DESTINATIONS: DestinationDef[] = [
  // Tier 1 — SEA + South Asia
  { slugPrefix: 'bali', displayName: 'Bali', countryCode: 'ID' },
  { slugPrefix: 'thailand', displayName: 'Thailand', countryCode: 'TH' },
  { slugPrefix: 'vietnam', displayName: 'Vietnam', countryCode: 'VN' },
  { slugPrefix: 'india', displayName: 'India', countryCode: 'IN' },
  { slugPrefix: 'philippines', displayName: 'Philippines', countryCode: 'PH' },
  { slugPrefix: 'malaysia', displayName: 'Malaysia', countryCode: 'MY' },
  { slugPrefix: 'singapore', displayName: 'Singapore', countryCode: 'SG' },
  { slugPrefix: 'indonesia', displayName: 'Indonesia', countryCode: 'ID' },
  { slugPrefix: 'japan', displayName: 'Japan', countryCode: 'JP' },
  // Tier 2 — Middle East + Africa
  { slugPrefix: 'egypt', displayName: 'Egypt', countryCode: 'EG' },
  { slugPrefix: 'saudi-arabia', displayName: 'Saudi Arabia', countryCode: 'SA' },
  { slugPrefix: 'turkey', displayName: 'Turkey', countryCode: 'TR' },
  { slugPrefix: 'uae', displayName: 'UAE', countryCode: 'AE' },
  { slugPrefix: 'ethiopia', displayName: 'Ethiopia', countryCode: 'ET' },
  { slugPrefix: 'morocco', displayName: 'Morocco', countryCode: 'MA' },
  { slugPrefix: 'sudan', displayName: 'Sudan', countryCode: 'SD' },
  { slugPrefix: 'seychelles', displayName: 'Seychelles', countryCode: 'SC' },
  { slugPrefix: 'mauritius', displayName: 'Mauritius', countryCode: 'MU' },
  { slugPrefix: 'tunisia', displayName: 'Tunisia', countryCode: 'TN' },
  { slugPrefix: 'madagascar', displayName: 'Madagascar', countryCode: 'MG' },
  { slugPrefix: 'rwanda', displayName: 'Rwanda', countryCode: 'RW' },
  { slugPrefix: 'senegal', displayName: 'Senegal', countryCode: 'SN' },
  { slugPrefix: 'zimbabwe', displayName: 'Zimbabwe', countryCode: 'ZW' },
  { slugPrefix: 'ghana', displayName: 'Ghana', countryCode: 'GH' },
  { slugPrefix: 'zambia', displayName: 'Zambia', countryCode: 'ZM' },
  { slugPrefix: 'mozambique', displayName: 'Mozambique', countryCode: 'MZ' },
  { slugPrefix: 'malawi', displayName: 'Malawi', countryCode: 'MW' },
  { slugPrefix: 'angola', displayName: 'Angola', countryCode: 'AO' },
  { slugPrefix: 'mali', displayName: 'Mali', countryCode: 'ML' },
  { slugPrefix: 'burkina-faso', displayName: 'Burkina Faso', countryCode: 'BF' },
  { slugPrefix: 'niger', displayName: 'Niger', countryCode: 'NE' },
  { slugPrefix: 'cote-d-ivoire', displayName: 'Côte d\'Ivoire', countryCode: 'CI' },
  { slugPrefix: 'chad', displayName: 'Chad', countryCode: 'TD' },
  { slugPrefix: 'cameroon', displayName: 'Cameroon', countryCode: 'CM' },
  { slugPrefix: 'burundi', displayName: 'Burundi', countryCode: 'BI' },
  { slugPrefix: 'benin', displayName: 'Benin', countryCode: 'BJ' },
  { slugPrefix: 'guinea', displayName: 'Guinea', countryCode: 'GN' },
  { slugPrefix: 'somalia', displayName: 'Somalia', countryCode: 'SO' },
  { slugPrefix: 'liberia', displayName: 'Liberia', countryCode: 'LR' },
  { slugPrefix: 'gabon', displayName: 'Gabon', countryCode: 'GA' },
  { slugPrefix: 'cabo-verde', displayName: 'Cabo Verde', countryCode: 'CV' },
  { slugPrefix: 'sierra-leone', displayName: 'Sierra Leone', countryCode: 'SL' },
  { slugPrefix: 'botswana', displayName: 'Botswana', countryCode: 'BW' },
  { slugPrefix: 'namibia', displayName: 'Namibia', countryCode: 'NA' },
  { slugPrefix: 'togo', displayName: 'Togo', countryCode: 'TG' },
  { slugPrefix: 'gambia', displayName: 'Gambia', countryCode: 'GM' },
  { slugPrefix: 'lesotho', displayName: 'Lesotho', countryCode: 'LS' },
  { slugPrefix: 'eswatini', displayName: 'Eswatini', countryCode: 'SZ' },
  { slugPrefix: 'djibouti', displayName: 'Djibouti', countryCode: 'DJ' },
  { slugPrefix: 'mauritania', displayName: 'Mauritania', countryCode: 'MR' },
  { slugPrefix: 'south-sudan', displayName: 'South Sudan', countryCode: 'SS' },
  { slugPrefix: 'eritrea', displayName: 'Eritrea', countryCode: 'ER' },
  { slugPrefix: 'guinea-bissau', displayName: 'Guinea-Bissau', countryCode: 'GW' },
  { slugPrefix: 'equatorial-guinea', displayName: 'Equatorial Guinea', countryCode: 'GQ' },
  { slugPrefix: 'libya', displayName: 'Libya', countryCode: 'LY' },
  { slugPrefix: 'central-african-republic', displayName: 'Central African Republic', countryCode: 'CF' },
  { slugPrefix: 'republic-of-the-congo', displayName: 'Republic of the Congo', countryCode: 'CG' },
  { slugPrefix: 'comoros', displayName: 'Comoros', countryCode: 'KM' },
  { slugPrefix: 'saint-helena', displayName: 'Saint Helena', countryCode: 'SH' },
  { slugPrefix: 'sao-tome-and-principe', displayName: 'Sao Tome and Principe', countryCode: 'ST' },
  { slugPrefix: 'mayotte', displayName: 'Mayotte', countryCode: 'YT' },
  { slugPrefix: 'western-sahara', displayName: 'Western Sahara', countryCode: 'EH' },
  { slugPrefix: 'reunion', displayName: 'Reunion', countryCode: 'RE' },
  // Tier 2 — East Asia + Oceania + Europe
  { slugPrefix: 'south-korea', displayName: 'South Korea', countryCode: 'KR' },
  { slugPrefix: 'mongolia', displayName: 'Mongolia', countryCode: 'MN' },
  { slugPrefix: 'australia', displayName: 'Australia', countryCode: 'AU' },
  { slugPrefix: 'united-kingdom', displayName: 'United Kingdom', countryCode: 'GB' },
  // Tier 2 — Southeast Asia remaining
  { slugPrefix: 'laos', displayName: 'Laos', countryCode: 'LA' },
  { slugPrefix: 'myanmar', displayName: 'Myanmar', countryCode: 'MM' },
];

// ---------------------------------------------------------------------------
// Data helpers
// ---------------------------------------------------------------------------

export interface CountryMonthData {
  destination: DestinationDef;
  month: number; // 1-indexed
  monthName: string;
  events: CalendarEvent[];
  seasonInfo: string | null;
}

export function getCountryMonthData(dest: DestinationDef, month: number): CountryMonthData {
  const events = getEventsForCountry(dest.countryCode)
    .filter(e => parseInt(e.dateStart.slice(5, 7), 10) === month);

  // Get season context from nature events
  const natureEvents = getEventsForNatureByCountry(dest.countryCode);
  const relevantNature = natureEvents.find(e => {
    const startMonth = parseInt(e.dateStart.slice(5, 7), 10);
    const endMonth = parseInt(e.dateEnd.slice(5, 7), 10);
    // Check if month falls within event range
    if (startMonth <= endMonth) return month >= startMonth && month <= endMonth;
    return month >= startMonth || month <= endMonth; // wraps around year
  });
  const seasonInfo = relevantNature?.title ?? null;

  return {
    destination: dest,
    month,
    monthName: MONTHS[month - 1],
    events,
    seasonInfo,
  };
}

/**
 * Months (1-indexed) for which a country×month page is actually generated for
 * the given destination. Uses the same data path and threshold as the provider,
 * so callers (e.g. MonthNavigation) never link to a page that returns 404.
 */
export function getGeneratedMonths(slugPrefix: string): number[] {
  const dest = DESTINATIONS.find(d => d.slugPrefix === slugPrefix);
  if (!dest) return [];
  const months: number[] = [];
  for (let month = 1; month <= 12; month++) {
    if (getCountryMonthData(dest, month).events.length >= MIN_EVENTS_FOR_PAGE) {
      months.push(month);
    }
  }
  return months;
}

/**
 * Country×month travel pages that actually exist for a country, as
 * `{ month, slug }` — only the generated months (never 404), using the
 * always-present English slug (mirrors MonthNavigation). Returns [] for
 * countries without a destination. Consumed by related-pages cross-linking.
 */
export function getCountryMonthLinks(code: CountryCode): { destination: string; months: Array<{ month: number; slug: string }> } {
  const dest = DESTINATIONS.find(d => d.countryCode === code);
  if (!dest) return { destination: '', months: [] };
  const months = getGeneratedMonths(dest.slugPrefix).map(month => ({
    month,
    slug: `${dest.slugPrefix}-in-${MONTH_NAMES[month - 1]}`,
  }));
  return { destination: dest.displayName, months };
}

// ---------------------------------------------------------------------------
// Evergreen provider
// ---------------------------------------------------------------------------

function provideCountryMonthSlugs(): EvergreenSlugEntry[] {
  const entries: EvergreenSlugEntry[] = [];

  for (const dest of DESTINATIONS) {
    const langs = getLanguagesForCountry(dest.countryCode);

    for (let month = 1; month <= 12; month++) {
      const data = getCountryMonthData(dest, month);

      // Only generate pages for months with enough events (see MIN_EVENTS_FOR_PAGE)
      if (data.events.length < MIN_EVENTS_FOR_PAGE) continue;

      const country = getCountryByCode(dest.countryCode);
      const countryFlag = country?.flag ?? '';
      const entityId = `country-month:${dest.slugPrefix}:${MONTH_NAMES[month - 1]}`;

      const seenSlugs = new Set<string>();
      for (const lang of langs) {
        const monthPhrase = getPhraseEntry(`month_${month}`, lang);
        const connector = getPhraseEntry('in_month', lang);
        const slug = `${dest.slugPrefix}-${connector.slug}-${monthPhrase.slug}`;

        // Skip languages whose phrases fall back to English, producing a duplicate slug
        if (seenSlugs.has(slug)) continue;
        seenSlugs.add(slug);

        // Localize the destination name on non-English pages when it IS the whole
        // country; sub-country destinations like Bali keep their curated label.
        // (almanak-kg1c)
        const enName = getCountryNameEntry(dest.countryCode, 'en').display;
        const localizedName = dest.displayName === enName
          ? getCountryNameEntry(dest.countryCode, lang).display
          : dest.displayName;

        const tplVars = {
          destination: localizedName,
          month: monthPhrase.display,
          year: YEAR,
          eventCount: data.events.length,
          siteName: SITE_NAME,
        };

        const pageTitle = getTemplateStr('country_month_title', lang, tplVars)
          || `${localizedName} ${connector.display} ${monthPhrase.display} ${YEAR} | ${SITE_NAME}`;
        const pageDescription = getTemplateStr('country_month_desc', lang, tplVars)
          || `${localizedName} ${connector.display} ${monthPhrase.display} ${YEAR}`;

        entries.push({
          pageType: 'country-month',
          slug,
          lang,
          entityId,
          pageTitle,
          pageDescription,
          h2Title: `${countryFlag} ${localizedName} ${connector.display} ${monthPhrase.display} ${YEAR}`,
          data: {
            countryMonthSlug: slug,
            slugPrefix: dest.slugPrefix,
            displayName: localizedName,
            countryCode: dest.countryCode,
            countryFlag,
            month,
            monthName: monthPhrase.display,
            eventCount: data.events.length,
          },
        });
      }
    }
  }

  return entries;
}

registerEvergreenProvider(provideCountryMonthSlugs);

// This provider gates slug generation on event counts, so it returns [] until
// events are loaded. Signal that readiness so the evergreen registry isn't
// frozen with country-month pages missing (almanak-kb0s).
registerEvergreenReadiness(() => getAllEvents().length > 0);

// ---------------------------------------------------------------------------
// Evergreen renderer
// ---------------------------------------------------------------------------

registerEvergreenRenderer('country-month', (entry, topPages) => {
  const slugPrefix = entry.data.slugPrefix as string;
  const month = entry.data.month as number;
  const dest = DESTINATIONS.find(d => d.slugPrefix === slugPrefix);
  if (!dest) return null;
  return CountryMonthSeoSection({ dest, month, entry });
});

// ---------------------------------------------------------------------------
// 404 recovery
// ---------------------------------------------------------------------------

/**
 * Recover dataless country-month 404s. Country-month pages are only generated
 * for months a destination actually has events for (see MIN_EVENTS_FOR_PAGE),
 * but crawlers speculate the other months — e.g. Turkey generates `turkey-in-may`
 * / `turkey-de-5yue` but not `turkey-in-march` / `turkey-de-3yue`, which 404.
 *
 * Detect a genuine country-month-shaped slug —
 * `{slugPrefix}-{in_month connector}-{month phrase}` for any month in any of the
 * destination's languages — and return the country's holiday page slug in that
 * same language so the [slug] fallback can 301 instead of 404. Reuses the exact
 * phrase logic the provider builds slugs with, so it never matches a non
 * country-month slug (e.g. `thailand-festivals`). Returns null otherwise.
 * Runs only after resolveSlug misses, so any match is by definition a slug that
 * would otherwise 404. (almanak-orx9)
 */
export function getCountryMonthRedirect(slug: string): string | null {
  for (const dest of DESTINATIONS) {
    const prefix = `${dest.slugPrefix}-`;
    if (!slug.startsWith(prefix)) continue;
    const remainder = slug.slice(prefix.length);

    for (const lang of getLanguagesForCountry(dest.countryCode)) {
      const connector = getPhraseEntry('in_month', lang).slug;
      for (let month = 1; month <= 12; month++) {
        const monthSlug = getPhraseEntry(`month_${month}`, lang).slug;
        if (remainder === `${connector}-${monthSlug}`) {
          const countrySlug = getSlugForCountry(dest.countryCode, lang);
          return countrySlug && countrySlug !== slug ? countrySlug : null;
        }
      }
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Exports for component use
// ---------------------------------------------------------------------------

export { DESTINATIONS, MONTH_NAMES, MONTHS as MONTH_DISPLAY };
export type { DestinationDef };
